import { usePermissions } from '../hooks/usePermissions';
import { UserRole } from '../contexts/PermissionsContext';

export const useAuthorization = () => {
  const { permissions, hasPermission, canViewCatalog, canEditCatalog, canBulkOperation } = usePermissions();

  const isRole = (role: UserRole): boolean => {
    return permissions?.role === role;
  };

  const isAdmin = (): boolean => isRole('admin');
  const isCatalogOwner = (): boolean => isRole('catalog-owner');
  const isViewer = (): boolean => isRole('viewer');
  const isDelegate = (): boolean => isRole('delegate');
  const isLegacyManager = (): boolean => isRole('legacy-manager');

  const hasSubscription = (tier?: string): boolean => {
    if (!tier) return !!permissions?.subscriptionTier;
    return permissions?.subscriptionTier === tier;
  };

  const isCreatorTier = (): boolean => hasSubscription('creator');
  const isCommercialTier = (): boolean => hasSubscription('commercial');

  const canManageUsers = (): boolean => isAdmin();
  const canViewAnalytics = (catalogId?: string): boolean => {
    if (isAdmin()) return true;
    if (!catalogId) return false;
    return hasPermission('ViewAnalytics', { catalogId });
  };

  const canCreateCatalog = (): boolean => {
    return isCatalogOwner() || isAdmin();
  };

  const canDelegateAccess = (catalogId: string): boolean => {
    return hasPermission('DelegateAccess', { catalogId });
  };

  const getMaxDelegates = (): number => {
    if (isAdmin()) return Infinity;
    if (isCommercialTier()) return 10;
    if (isCreatorTier()) return 3;
    return 0;
  };

  const getMaxCatalogItems = (): number => {
    if (isAdmin()) return Infinity;
    if (isCommercialTier()) return 650;
    return Infinity;
  };

  const getMaxLegacyManagers = (): number => {
    if (isAdmin()) return Infinity;
    return 3; // Both creator and commercial get 3
  };

  const getBulkOperationLimit = (): number => {
    if (isAdmin() || isCommercialTier()) return Infinity;
    if (isCreatorTier()) return 100;
    return 0;
  };

  return {
    permissions,
    isRole,
    isAdmin,
    isCatalogOwner,
    isViewer,
    isDelegate,
    isLegacyManager,
    hasSubscription,
    isCreatorTier,
    isCommercialTier,
    hasPermission,
    canViewCatalog,
    canEditCatalog,
    canBulkOperation,
    canManageUsers,
    canViewAnalytics,
    canCreateCatalog,
    canDelegateAccess,
    getMaxCatalogItems,
    getMaxDelegates,
    getMaxLegacyManagers,
    getBulkOperationLimit,
  };
};
